import { useState } from 'react';
import {
  App,
  Button,
  Card,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import {
  CloseCircleOutlined,
  ReloadOutlined,
  SendOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useCancelCommand, useCommands } from '@/hooks/useCommands';
import type { CommandDto } from '@/types/command';
import {
  CommandStatusColors,
  CommandStatusLabels,
  CommandStatusMap,
  CommandTypeLabels,
  CommandTypeMap,
} from '@/types/command';

const { Title, Text } = Typography;

export function CommandsPage() {
  const { message } = App.useApp();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState<number | undefined>();
  const [typeFilter, setTypeFilter] = useState<number | undefined>();

  const { data, isLoading, refetch, isFetching } = useCommands({
    page,
    pageSize,
    status: statusFilter,
    commandType: typeFilter,
  });

  const cancelMut = useCancelCommand();

  const handleCancel = async (c: CommandDto) => {
    try {
      await cancelMut.mutateAsync(c.id);
      message.success('Comando cancelado.');
    } catch {
      // interceptor
    }
  };

  const columns: TableProps<CommandDto>['columns'] = [
    {
      title: 'Tipo',
      dataIndex: 'commandType',
      key: 'commandType',
      width: 160,
      render: (v: number) => {
        const t = CommandTypeMap[v];
        return (
          <Space>
            <ThunderboltOutlined />
            <Text strong>{CommandTypeLabels[t]}</Text>
          </Space>
        );
      },
    },
    {
      title: 'Dispositivo',
      dataIndex: 'deviceLabel',
      key: 'deviceLabel',
    },
    {
      title: 'Solicitado por',
      dataIndex: 'requestedByName',
      key: 'requestedByName',
      width: 180,
    },
    {
      title: 'Fecha',
      dataIndex: 'requestedAt',
      key: 'requestedAt',
      width: 160,
      render: (v) => dayjs(v).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (v: number) => {
        const s = CommandStatusMap[v];
        return <Tag color={CommandStatusColors[s]}>{CommandStatusLabels[s]}</Tag>;
      },
    },
    {
      title: 'Resultado',
      key: 'result',
      render: (_, c) => {
        if (c.errorMessage) return <Tooltip title={c.errorMessage}><Text type="danger">{c.errorMessage.slice(0, 60)}{c.errorMessage.length > 60 ? '...' : ''}</Text></Tooltip>;
        if (c.responseMessage) return <Text type="secondary">{c.responseMessage}</Text>;
        return <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 110,
      align: 'right',
      render: (_, c) => {
        if (CommandStatusMap[c.status] !== 'Pending') return null;
        return (
          <Popconfirm
            title="Cancelar este comando?"
            onConfirm={() => handleCancel(c)}
            okText="Si"
            cancelText="No"
          >
            <Button danger size="small" icon={<CloseCircleOutlined />} loading={cancelMut.isPending}>
              Cancelar
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  const statusOptions = Object.entries(CommandStatusMap).map(([k, v]) => ({
    value: Number(k),
    label: CommandStatusLabels[v],
  }));

  const typeOptions = Object.entries(CommandTypeMap).map(([k, v]) => ({
    value: Number(k),
    label: CommandTypeLabels[v],
  }));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>
          <SendOutlined style={{ marginRight: 12 }} />
          Comandos remotos
        </Title>
      </div>

      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Select
            placeholder="Estado"
            allowClear
            value={statusFilter}
            onChange={(v) => { setStatusFilter(v); setPage(1); }}
            options={statusOptions}
            style={{ width: 160 }}
          />
          <Select
            placeholder="Tipo"
            allowClear
            value={typeFilter}
            onChange={(v) => { setTypeFilter(v); setPage(1); }}
            options={typeOptions}
            style={{ width: 180 }}
          />
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            Refrescar
          </Button>
          <Text type="secondary">Auto-refresh cada 5seg</Text>
        </Space>

        <Table<CommandDto>
          rowKey="id"
          columns={columns}
          dataSource={data?.items ?? []}
          loading={isLoading}
          pagination={{
            current: page,
            pageSize,
            total: data?.totalCount ?? 0,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (total) => `${total} en total`,
          }}
        />
      </Card>
    </div>
  );
}
